import { useState } from 'react';

import type { ConsentGate } from '@toothsomatics/consent-catalog';
import {
  SERVICE_1_QUESTIONNAIRE_V1,
  type Question,
  type QuestionGroup,
} from '@toothsomatics/questionnaire';

import { ConsentChecklist } from '../../components/ConsentChecklist.js';
import './questionnaire.css';

/**
 * The questionnaire, rendered from data rather than written as markup.
 *
 * Every question comes from `@toothsomatics/questionnaire`, which is the same definition the API
 * seeds and validates against. Nothing here is hard-coded, so a reworded question is a content
 * change in one place and never a component edit.
 *
 * The optional group carries a consent gate. This screen shows what the gate covers and asks for it
 * plainly, but it does not decide anything: the API refuses to store an answer in that group
 * without a consent record, whatever this component believes.
 */

export type Answers = Readonly<Record<string, string>>;

/**
 * The four acknowledgements the client's build brief asks for at the bottom of the intake form
 * (section 4.4), in its order. Each is its own gate and its own record — the brief lists them as
 * four separate boxes and the constitution would require that anyway.
 */
export const INTAKE_GATES: readonly ConsentGate[] = [
  'questionnaire_not_professional_care',
  'emotional_awareness_responsibility',
  'submission_interpretation_responsibility',
  'sensitive_information_optional',
];

export interface QuestionnaireProps {
  readonly answers: Answers;
  readonly onChange: (key: string, value: string) => void;
  readonly onBack: () => void;
  readonly consents: Readonly<Partial<Record<ConsentGate, boolean>>>;
  readonly onConsentChange: (gate: ConsentGate, value: boolean) => void;
  readonly onSubmit: () => void;
  readonly submitting: boolean;
  readonly submitError: string | null;
}

export function Questionnaire({
  answers,
  onChange,
  onBack,
  consents,
  onConsentChange,
  onSubmit,
  submitting,
  submitError,
}: QuestionnaireProps) {
  const version = SERVICE_1_QUESTIONNAIRE_V1;

  const missing = version.groups
    .flatMap((group) => group.questions)
    .filter((question) => question.required && (answers[question.key] ?? '').trim() === '');

  // The four acknowledgements are all required (the brief, section 4.4). Rather than disable the
  // button — which leaves someone guessing why — it stays live and, on a click that is not yet
  // ready, says exactly what remains. The API still re-checks the submit gate and refuses without
  // it; this only spares a round trip.
  const untickedConsents = INTAKE_GATES.filter((gate) => consents[gate] !== true);

  const whatsLeft: string[] = [];
  if (missing.length > 0) {
    whatsLeft.push(
      `answer ${missing.map((question) => `“${question.label}”`).join(', ')}`,
    );
  }
  if (untickedConsents.length > 0) {
    whatsLeft.push(
      `tick all ${String(INTAKE_GATES.length)} acknowledgements below (${String(untickedConsents.length)} left)`,
    );
  }
  const ready = whatsLeft.length === 0;

  // Set the first time submit is pressed while something is still missing, so the guidance turns
  // into a visible complaint rather than the button appearing to do nothing.
  const [nudged, setNudged] = useState(false);

  const attemptSubmit = () => {
    if (ready) {
      onSubmit();
      return;
    }
    // Not ready: never silent. Show what remains and take the person to it.
    setNudged(true);
    const target = document.getElementById(
      untickedConsents.length > 0 ? 'consents-heading' : missing[0] ? missing[0].key : 'group-about_you',
    );
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="page__body page__body--form">
      {/*
        The welcome holds still on the left while the form scrolls on the right — the same shape as
        the chart step, where the arch stays and the panel moves. It is read once and then referred
        back to, which is exactly what a column that does not move is for.
      */}
      <aside className="welcome">
        {version.welcome.map((paragraph, index) => (
          <p key={paragraph.slice(0, 24)} className={index === 0 ? 'welcome__hello' : ''}>
            {paragraph}
          </p>
        ))}
      </aside>

      <div className="form">
        <div className="form__body">
          {version.groups.map((group) => (
            <Group key={group.key} group={group} answers={answers} onChange={onChange} />
          ))}

          {/* Inside the scrolling body: they belong to the form, at the end of it. */}
          <ConsentChecklist
            heading="Before you submit"
            intro="Four things to read and agree to. Each is separate and each is recorded on its own — nothing here is bundled into a single tick."
            gates={INTAKE_GATES}
            accepted={consents}
            onChange={onConsentChange}
          />
        </div>

        <footer className="form__foot">
          {/*
            The readiness statement is a courtesy, not a gate. Whether this may be submitted is the
            API's answer, behind Gate 4.
          */}
          <p
            className={
              submitError || (nudged && !ready)
                ? 'form__readiness form__readiness--error'
                : 'form__readiness'
            }
          >
            {submitError
              ? submitError
              : ready
                ? 'Everything needed is here. You can submit whenever you are ready.'
                : `Before you can submit, please ${whatsLeft.join(', and ')}.`}
          </p>

          <div className="form__actions">
            <button type="button" className="button button--quiet" onClick={onBack}>
              Back to your chart
            </button>
            <button
              type="button"
              className="button button--primary"
              disabled={submitting}
              aria-disabled={!ready}
              onClick={attemptSubmit}
            >
              {submitting ? 'Submitting…' : 'Submit my reflection'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

interface GroupProps {
  readonly group: QuestionGroup;
  readonly answers: Answers;
  readonly onChange: (key: string, value: string) => void;
}

function Group({ group, answers, onChange }: GroupProps) {
  return (
    <section className="group" aria-labelledby={`group-${group.key}`}>
      <h2 className="group__title" id={`group-${group.key}`}>
        {group.title}
      </h2>
      {group.intro ? <p className="group__intro">{group.intro}</p> : null}

      {group.gate ? (
        <p className="group__gate">
          Before anything in this section is stored, you will be asked to agree to it on its own —
          separately from everything else, and you may decline and still continue.
        </p>
      ) : null}

      {/*
        Short answers pair up on a wide screen and a long one always takes the full measure.
        A one-line name in a 68-character box reads as an unanswered essay question.
      */}
      <div className="fields">
        {group.questions.map((question) => (
          <Field
            key={question.key}
            question={question}
            value={answers[question.key] ?? ''}
            onChange={(value) => onChange(question.key, value)}
          />
        ))}
      </div>
    </section>
  );
}

interface FieldProps {
  readonly question: Question;
  readonly value: string;
  readonly onChange: (value: string) => void;
}

function Field({ question, value, onChange }: FieldProps) {
  const helpId = question.help ? `${question.key}-help` : undefined;
  const wide = question.type === 'long_text';

  return (
    <div className={wide ? 'field field--wide' : 'field'}>
      <label className="field__label" htmlFor={question.key}>
        {question.label}
        {question.required ? (
          <span className="field__required"> (needed)</span>
        ) : (
          <span className="field__optional"> (optional)</span>
        )}
      </label>

      {question.help ? (
        <p className="field__help" id={helpId}>
          {question.help}
        </p>
      ) : null}

      {question.type === 'long_text' ? (
        <textarea
          id={question.key}
          className="field__input field__input--long"
          rows={5}
          value={value}
          maxLength={question.maxLength}
          aria-describedby={helpId}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          id={question.key}
          className="field__input"
          type={question.type === 'date' ? 'date' : 'text'}
          value={value}
          maxLength={question.maxLength}
          aria-describedby={helpId}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </div>
  );
}
